import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";

import tajawalFont from "../../assets/font/Tajawal-Regular.ttf";

Font.register({
  family: "Tajawal",
  fonts: [
    {
      src: tajawalFont,
    },
  ],
});

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 20,
    fontSize: 10,
    fontFamily: "Tajawal",
  },
  arabicText: {
    fontFamily: "Tajawal",
    textAlign: "right",
    direction: "rtl",
  },
  englishText: {
    fontFamily: "Tajawal",
    textAlign: "left",
    direction: "ltr",
  },
  header: {
    textAlign: "center",
    fontSize: 24,
    fontWeight: "bold",
    color: "#79329a",
    marginBottom: 5,
    paddingBottom: 5,
  },
  line: {
    borderBottomWidth: 2,
    borderBottomColor: "#79329a",
    marginBottom: 30,
  },
  dateSection: {
    textAlign: "center",
    fontSize: 12,
    marginBottom: 20,
    color: "#6b7280",
  },
  tableContainer: {
    marginBottom: 20,
  },
  tableWrapper: {
    flexDirection: "column",
    borderWidth: 1,
    borderColor: "#d1d5db",
    minPresenceAhead: 100,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#e5e7eb",
    borderBottomWidth: 2,
    borderBottomColor: "#9ca3af",
    fontWeight: "bold",
  },
  tableHeaderRTL: {
    flexDirection: "row-reverse",
    backgroundColor: "#e5e7eb",
    borderBottomWidth: 2,
    borderBottomColor: "#9ca3af",
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
  },
  tableRowRTL: {
    flexDirection: "row-reverse",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
  },
  tableRowContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
    orphans: 3,
    widows: 3,
  },
  tableCell: {
    flex: 1,
    padding: 8,
    fontSize: 10,
    textAlign: "center",
    borderRightWidth: 1,
    borderRightColor: "#d1d5db",
  },
  tableCellLast: {
    flex: 1,
    padding: 8,
    fontSize: 10,
    textAlign: "center",
  },
  summarySection: {
    marginTop: 20,
    gap: 10,
  },
  summaryBox: {
    padding: 15,
    borderRadius: 5,
    backgroundColor: "#ddecf6",
    borderWidth: 1,
    borderColor: "#426f8c",
    textAlign: "start",
    width: "25%",
  },
  boxTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
  },
  boxAmount: {
    fontSize: 16,
    fontWeight: "bold",
  },
  flexRow: {
    display: "flex",
    justifyContent: "space-between",
  },
});

const StudentsStatementTemplate = ({
  language = "EN",
  companyName = "Your Company Name",
  students = [],
  schoolName = "",
}) => {
  const isArabic = language === "AR";

  // Translations
  const translations = {
    EN: {
      schoolName: "School Name",
      currentDate: "Report Date",
      studentName: "Student Name",
      class: "Class",
      subClass: "Sub-class",
      sequence: "Sequence",
      studentReport: "Students Information",
      totalStudents: "Total Students",
    },
    AR: {
      schoolName: "إسم المدرسة",
      currentDate: "تاريخ الكشف",
      studentName: "اسم التلميذ",
      class: "الصف",
      subClass: "الشعبة",
      sequence: "التسلسل",
      alhabbarSlogan: "شركة الحبّار للتطوير البرمجي",
      ehsiblySecondSlogan: "برنامج إحسبلي للإدارة والمحاسبة",
      studentReport: "معلومات التلاميذ",
      totalStudents: "إجمالي التلاميذ",
    },
  };

  const t = translations[language] || translations.EN;

  // Get current date
  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-UK");
  };

  // Helper component for styled text
  const StyledText = ({ children, style = {} }) => {
    const baseStyle = isArabic ? styles.arabicText : styles.englishText;
    return <Text style={{ ...baseStyle, ...style }}>{children}</Text>;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header - Company Name */}
        <View style={styles.line}>
          <StyledText style={styles.header}>{companyName}</StyledText>
          <StyledText style={styles.dateSection}>{t.studentReport}</StyledText>
        </View>

        {/* Current Date */}
        <View style={styles.flexRow}>
          <StyledText style={styles.dateSection}>
            {t.schoolName}: {schoolName}
          </StyledText>
          <StyledText style={styles.dateSection}>
            {t.currentDate}: {getCurrentDate()}
          </StyledText>
        </View>

        {/* Students Table */}
        <View style={styles.tableContainer}>
          <View style={styles.tableWrapper}>
            {/* Table Header */}
            <View style={isArabic ? styles.tableHeaderRTL : styles.tableHeader}>
              <StyledText style={styles.tableCell}>{t.sequence}</StyledText>
              <StyledText style={styles.tableCell}>{t.studentName}</StyledText>
              <StyledText style={styles.tableCell}>{t.class}</StyledText>
              <StyledText style={styles.tableCellLast}>{t.subClass}</StyledText>
            </View>

            {/* Table Rows */}
            {students.map((student, index) => (
              <View
                key={index}
                style={[
                  isArabic ? styles.tableRowRTL : styles.tableRow,
                  styles.tableRowContainer,
                ]}
              >
                {isArabic ? (
                  <>
                    <StyledText style={styles.tableCell}>
                      {student.activeYear.schoolSubClass?.name || ""}
                    </StyledText>
                    <StyledText style={styles.tableCell}>
                      {student.activeYear.schoolClass?.name || ""}
                    </StyledText>
                    <StyledText style={styles.tableCell}>
                      {student.fullName || ""}
                    </StyledText>
                    <StyledText style={styles.tableCell}>
                      {student.sequence || index + 1}
                    </StyledText>
                  </>
                ) : (
                  <>
                    <StyledText style={styles.tableCell}>
                      {student.sequence || index + 1}
                    </StyledText>
                    <StyledText style={styles.tableCell}>
                      {student.fullName || ""}
                    </StyledText>
                    <StyledText style={styles.tableCell}>
                      {student.activeYear.schoolClass?.name || ""}
                    </StyledText>
                    <StyledText style={styles.tableCell}>
                      {student.activeYear.schoolSubClass?.name || ""}
                    </StyledText>
                  </>
                )}
              </View>
            ))}
          </View>
        </View>

        {/* Summary Box */}
        <View style={styles.summarySection}>
          <View
            style={{ ...styles.summaryBox, marginLeft: isArabic ? "auto" : 0 }}
          >
            <StyledText style={styles.boxTitle}>{t.totalStudents}</StyledText>
            <StyledText style={styles.boxAmount}>{students.length}</StyledText>
          </View>
        </View>

        {/* Footer Section */}
        <View
          style={{
            position: "absolute",
            bottom: 20,
            left: 20,
            right: 20,
            textAlign: "center",
            paddingTop: 10,
            borderTopWidth: 1,
            borderTopColor: "#d1d5db",
            flexDirection: isArabic ? "column-reverse" : "column",
            alignItems: "center",
          }}
        >
          <Text
            style={{
              fontSize: 8,
              fontFamily: "Tajawal",
            }}
          >
            {t.alhabbarSlogan}
          </Text>
          <Text
            style={{
              fontSize: 10,
              fontWeight: "bold",
              marginBottom: 4,
              fontFamily: "Tajawal",
            }}
          >
            {t.ehsiblySecondSlogan}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default StudentsStatementTemplate;
